import React, { createContext, useContext, useState } from 'react';
import Cookies from 'js-cookie';

const DEFAULT_CURRENCY = 'GBP';

/* ========================================================================= */
/* Context(s) */
/* ========================================================================= */

/** @type {import('$types/components/contexts').ExchangeRateContext} */
const ExchangeRateContext = createContext({
    exchangeRate: null,
    currency: DEFAULT_CURRENCY,
    convertToCurrency: ({ amount }) => amount,
    setCurrency: () => {}
});

/* ========================================================================= */
/* Hook(s) */
/* ========================================================================= */

const useExchangeRate = () => {
    const context = useContext(ExchangeRateContext);

    if (context === undefined) {
        throw new Error('useExchangeRate must be used within a ExchangeRateProvider.');
    }

    return context;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/** @type {import('$types/components/contexts').ExchangeRateContextProvider} */
const ExchangeRateProvider = ({
    children,
    exchangeRate,
    defaultCurrency = DEFAULT_CURRENCY
}) => {
    /** @type {import('$types/components/contexts').CurrencyState} */
    const [currency, setCurrency] = useState(() => {
        try {
            const firstPathSegment = (typeof window !== 'undefined'
                ? window.location.pathname.split('/').filter(Boolean)[0]
                : '') || '';
            const stored = Cookies.get(`${firstPathSegment}_currency_cookie`);
            if (stored) { return JSON.parse(stored); }
        } catch {}
        return defaultCurrency;
    });

    /** @type {import('$types/components/contexts').ConvertCurrencyFn} */
    const convertToCurrency = ({
        from,
        to,
        amount
    }) => {
        const rate = exchangeRate.USD / exchangeRate[from ?? currency];
        return amount * exchangeRate[to] * rate;
    };

    return (
        <ExchangeRateContext.Provider value={{
            exchangeRate,
            convertToCurrency,
            currency,
            setCurrency,
        }}>
            {children}
        </ExchangeRateContext.Provider>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ExchangeRateContext.displayName = 'ExchangeRateContext';

export {
    ExchangeRateProvider,
    useExchangeRate
};
